/**
 * LINKURIOUS CONFIDENTIAL
 * Copyright Linkurious SAS 2012 - 2018
 *
 * Created by andrebarata on 2018-08-22.
 */

'use strict';

import {
  AfterViewInit,
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  Renderer2
} from '@angular/core';
import {Tools} from "linkurious-shared";

const NAVIGATION_MAPPING = [38, 39, 37, 40, 13, 9, 27];

@Directive({
  selector: '[keyboard-navigation]'
})
export class KeyboardNavigationDirective implements OnInit, AfterViewInit, OnDestroy {

  @Input() currentIndex: number = 0;
  @Input() closeOnTop: boolean = false;
  @Output() close: EventEmitter<any> = new EventEmitter();
  private _rootIndex: number;
  private _initialListener: () => void;

  constructor(
    private _render: Renderer2,
    private _elementRef: ElementRef,
    private _ngZone: NgZone
  ) {
  }

  ngOnInit() {
    this._ngZone.runOutsideAngular(() => {
      document.addEventListener('click', this.mouseClick.bind(this));
      document.addEventListener('mousemove', this.mouseMove.bind(this));
      document.addEventListener('keydown', this.keyDown.bind(this));
    });
  }

  ngAfterViewInit() {
    if ( Tools.isDefined(this.rootList[0]) ) {
      this._initialListener = this._render.listen(this.rootList[0], 'focus', (e: MouseEvent) => {
        if (
          !this._elementRef.nativeElement.contains(e.relatedTarget)
          && this.rootList[0].classList.contains('disabled')
        ) {
          this.focusItem('down');
        }
      });
    }
  }

  ngOnDestroy() {
    if (this._initialListener) {
      this._initialListener();
    }
    document.removeEventListener('click', this.mouseClick.bind(this));
    document.removeEventListener('mousemove', this.mouseMove.bind(this));
    document.removeEventListener('keydown', this.keyDown.bind(this));
  }

  private targetIsInMenu(target: any): boolean {
    for ( ; target && target !== document; target = target.parentNode ) {
      if ( target.matches('menu') ) {
        return true;
      }
    }
    return false;
  }

  /**
   * Store the hovered item
   */
  private mouseMove(event: MouseEvent): void {
    let target = event.target as HTMLElement;
    let hoveredEl = (
      (this._elementRef.nativeElement.contains(target) ||
        (
          document.querySelectorAll('.menu_list.is__sub_menu.active')[0] &&
          document.querySelectorAll('.menu_list.is__sub_menu.active')[0].contains(target)
        )) && target.tagName === 'BUTTON'
    )
      ? target as HTMLButtonElement | HTMLAnchorElement
      : undefined;
    if (!this.targetIsInMenu(target)) {
      this.reset();
    } else if ( Tools.isDefined(hoveredEl) ) {
      let isElementInSubList = false;
      let rootIndexToStore;
      if (
        document.querySelectorAll('.menu_list.is__sub_menu.active')[0] &&
        document.querySelectorAll('.menu_list.is__sub_menu.active')[0].contains(target)
      ) {
        isElementInSubList = true;
        rootIndexToStore = this.getElementIndex(document.querySelectorAll('button.menu_action.has_subMenu')[0].parentElement);
      }
      if (isElementInSubList) {
        this._rootIndex = rootIndexToStore;
        this.currentIndex = this.getElementIndex(hoveredEl.parentElement);
        this.childLevelItem.focus();
      } else {
        this._rootIndex = undefined;
        this.currentIndex = this.getElementIndex(hoveredEl.parentElement);
        for (let i = 0; i < this.rootList.length; ++i) {
          this.rootList[i].classList.remove('active');
        }
        if (this.rootLevelItem(this.currentIndex).classList.contains('has_subMenu')) {
          this.rootLevelItem(this.currentIndex).classList.add('active');
        }
        this.rootLevelItem(this.currentIndex).focus();
      }
    }
  }

  /*
  * Listening keyboard events
  */
  private keyDown(event: KeyboardEvent) {
    // Avoid triggering the navigation if the list is not focused
    if (this._elementRef.nativeElement.contains(document.activeElement) ||
      (
        document.querySelectorAll('.menu_list.is__sub_menu.active')[0] &&
        document.querySelectorAll('.menu_list.is__sub_menu.active')[0].contains(document.activeElement)
      )) {
      if ( NAVIGATION_MAPPING.indexOf(event.keyCode) >= 0 ) {
        event.preventDefault();
        event.stopPropagation();

        switch (event.keyCode) {
          case 38 : // Arrow up
            this.focusItem('up');
            break;

          case 40 : // Arrow down
            this.focusItem('down');
            break;

          case 39 : // arrow right
            if (this.rootLevelItem(this.currentIndex).classList.contains('has_subMenu')) {
              this._rootIndex = this.currentIndex;
              this.currentIndex = 0;
              if (!this.rootLevelItem(this._rootIndex).classList.contains('active')) {
                this.toggleActiveClass();

              }
              setTimeout(() => {
                this.childLevelItem.focus();
              }, 30);
            }
            break;

          case 37 : // arrow left
            if (Tools.isDefined(this._rootIndex)) {
              this.toggleActiveClass();
              this.rootLevelItem(this._rootIndex).focus();
              this.currentIndex = this._rootIndex;
              this._rootIndex = undefined;
            }
            break;

          case 13 : // Enter
          case 9: // Tab
            this.triggerClick();
            this.reset();
            break;

          case 27 : // Esc
            this.close.emit();
            this.reset();
            break;
        }
      }
    }
  }

  /*
  * Reset navigation when the list is clicked by the mouse
  */
  private mouseClick() {
    this.reset();
  }

  /**
   * Focus an item in list or sub-list based on a direction
   */
  private focusItem(direction: 'up' | 'down'): void {
    if ( direction === 'up' && this.currentIndex === 0 && this.closeOnTop ) {
      this.close.emit();
      this.reset();
    } else {
      let nextChild;
      let listLength = (this._rootIndex) ? this.childList.length : this.rootList.length;
      if (direction === 'up') {
        this.currentIndex = (this.currentIndex === 0) ? listLength - 1 : this.currentIndex - 1;
      } else {
        this.currentIndex = (this.currentIndex === listLength - 1) ? 0 : this.currentIndex + 1;
      }
      if (this._rootIndex) {
        nextChild = this.childLevelItem;
      } else {
        nextChild = this.rootLevelItem(this.currentIndex);
      }
      if (nextChild && nextChild.classList.contains('disabled')) {
        this.focusItem(direction);
      } else if ( nextChild ) {
        nextChild.focus();
      }
    }
  }

  /**
   * Add or remove the class 'active' from the parent of a sub-menu
   */
  private toggleActiveClass(): void {
    if (this.rootLevelItem(this._rootIndex).classList.contains('active')) {
      this.rootLevelItem(this._rootIndex).classList.remove('active');
    } else {
      this.rootLevelItem(this._rootIndex).classList.add('active');
    }
  }

  /**
   * Return the list of button at the root level
   */
  private get rootList(): HTMLCollection {
    return this._elementRef.nativeElement.firstChild.querySelectorAll('menu-item button');
  }

  /**
   * Return the list of button at the sub-menu level
   */
  private get childList(): HTMLCollection {
    return document.querySelectorAll('.menu_list.is__sub_menu.active menu-item button') as HTMLCollection;
  }

  /**
   * Return a button at the root level
   */
  private rootLevelItem(i: number): HTMLButtonElement | HTMLAnchorElement {
    return this._elementRef.nativeElement.firstChild.querySelectorAll('menu-item button')[i];
  }

  /**
   * Return a button at the sub-menu level
   */
  private get childLevelItem(): HTMLButtonElement | HTMLAnchorElement {
    return document.querySelectorAll('.menu_list.is__sub_menu.active menu-item button')[this.currentIndex] as HTMLButtonElement;
  }

  /**
   * Return the index of a element
   */
  private getElementIndex(node: Element): number {
    let i = 0;
    while ((node = node.previousElementSibling)) {
      i++;
    }
    return i;
  }

  /**
   * Trigger the click event of an item
   */
  private triggerClick() {
    if (this._rootIndex) {
      this.childLevelItem.click();
    } else {
      this.rootLevelItem(this.currentIndex).click();
    }
  }

  /*
  * Reset navigation
  */
  private reset() {
    if ( Tools.isDefined(this._rootIndex) ) {
      if ( this.childLevelItem ) {
        if ( this.rootList[this._rootIndex] && document.querySelectorAll('.menu_list.is__sub_menu.active')[0]) {
          this.rootList[this._rootIndex].classList.remove('active');
          this.childLevelItem.blur();
          document.querySelectorAll('.menu_list.is__sub_menu.active')[0].classList.remove('active');
        }
      }
    } else if ( this.rootLevelItem(this.currentIndex) ) {
      if(this.rootLevelItem(this.currentIndex).classList.contains('active')) {
        this.rootLevelItem(this.currentIndex).classList.remove('active');
      }
      this.rootLevelItem(this.currentIndex).blur();
    }
    this.currentIndex = 0;
    this._rootIndex = undefined;
  }
}
